<script setup lang="ts">

import {useCheckoutStore} from "@/checkout/stores/checkout.store.ts";
import {computed, ref} from "vue";
import {useI18n} from "vue-i18n";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/shared/components/ui/collapsible";
import {CaretSortIcon} from '@radix-icons/vue'
import {Badge} from "@/shared/components/ui/badge";
import {DesktopView} from "@/shared/components/ui/viewport";
import MobileAndTabletView from "@/shared/components/ui/viewport/MobileAndTabletView.vue";
import {MediaObject} from "@/shared/components/ui/mediaobject";

const checkout = useCheckoutStore();
const {t} = useI18n()

const isOpen = ref(false)
const deliveryCost = computed(() => checkout.cost?.deliveryCost?.[checkout.displayVatTotals ? "totalExcl" : "totalIncl"]?.formatted)
const displayVatTotals = computed(() => checkout.displayVatTotals)
const priceField = computed(() => checkout.displayVatTotals ? 'excl' : 'incl')
const totalItemCount = computed(() => {
  return checkout.items ? checkout.items.reduce((total, item) => total + item.quantity, 0) : 0;
});

const mobileSummary = ref<boolean>(false)
</script>

<template>
  <MobileAndTabletView>
    <div class="fixed inset-x-0 bottom-0 flex flex-col-reverse text-sm font-medium text-gray-900 lg:hidden ">
      <div class="relative z-10 border-t border-gray-200 bg-gray-50 px-4 sm:px-6">
        <div class="mx-auto max-w-lg">
          <button type="button" @click="mobileSummary = !mobileSummary" class="flex w-full items-center py-6 font-medium" aria-expanded="false">
            <span class="mr-auto text-base">{{ t('shared.common.total') }}</span>
            <span class="mr-2 text-base" v-if="displayVatTotals" v-html="checkout.cost?.totalPriceExcl?.formatted"></span>
            <span class="mr-2 text-base" v-else v-html="checkout.cost?.totalPriceIncl?.formatted"></span>
            <svg class="h-5 w-5 text-slate-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd"
                    d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                    clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      <div v-if="mobileSummary">
        <div class="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true"></div>
        <div class="relative bg-white px-4 py-6 sm:px-6">
          <dl class="mx-auto max-w-lg space-y-6">
            <div class="flex items-center justify-between">
              <dt class="text-slate-500">{{ t('shipping') }}
                <template v-if="checkout.deliveryMethod?.title !== ''">({{ checkout.deliveryMethod?.title }})</template>
              </dt>
              <dd v-html="deliveryCost"/>
            </div>
            <div class="flex items-center justify-between">
              <dt class="text-slate-500">{{ t('total_excl_vat') }}</dt>
              <dd v-html="checkout.cost?.subtotalPriceExcl?.formatted"/>
            </div>
            <div v-if="displayVatTotals" v-for="vatTotal in checkout.cost.vatTotals" class="flex items-center justify-between">
              <dt class="text-slate-500">{{ t('vat') }} {{vatTotal.percentage.formatted}}</dt>
              <dd v-html="vatTotal.total.formatted"/>
            </div>
          </dl>
        </div>
      </div>
    </div>
  </MobileAndTabletView>
  <DesktopView>
    <section aria-labelledby="summary-heading"
             class="bg-gray-50 px-4 pb-10 pt-16 sm:px-6 lg:col-start-2 lg:row-start-1 lg:bg-transparent lg:px-0 lg:pb-16">
      <div class="mx-auto max-w-lg lg:max-w-none">
        <h2 id="summary-heading" class="text-lg font-medium text-gray-900 mb-default">{{ t('overview') }}</h2>

        <div class="w-full space-y-6 pb-6">
          <Collapsible v-model:open="isOpen">
            <CollapsibleTrigger class="flex justify-between w-full">
              <div class="flex items-center">
                <span>{{t('shared.basket.products')}}</span>
                <Badge class="ml-2" variant="dark">{{ totalItemCount }}</Badge>
              </div>
              <div class="h-6 flex pr-0" style="position: relative;right: -13px;">
                <span v-if="displayVatTotals" class="text-gray-900 text-right" v-html="checkout.cost?.subtotalPriceExcl?.formatted"/>
                <span v-else class="text-gray-900 text-right" v-html="checkout.cost?.subtotalPriceIncl?.formatted"/>
                <CaretSortIcon style="position: relative;top: 4px;"/>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent class="space-y-default pt-6">
              <div class="flex items-start align-top justify-between" v-for="item in checkout.items">
                <MediaObject :title="item.description.actual"
                             :image="item.image?.responsive?.url"
                             :badge="item.quantity?.toString()"
                             :show-image="true"
                             :max-width="250"
                             :badge-class="{ 'bg-green-600' : item['__typename'] == 'PromoLineItem'}"
                />
                <div class="text-slate-500 text-xs flex-shrink-0 flex ml-4">
                  <div class="flex flex-col items-end">
                    <div>
                      <span class="pr-1">{{ item.quantity }} x</span>
                      <span v-if="!item.totalPrice.free" v-html="item.unitPrice.price[priceField]"/>
                    </div>
                    <span v-if="item.totalPrice.free && item['variant'].type == 'LOAN'" class="text-green-600 font-bold">{{t('shared.common.loan')}}</span>
                    <span v-if="item.totalPrice.free && item['variant'].type == 'GIFT'" class="text-green-600 font-bold">{{t('shared.common.gift')}}</span>
                    <span v-else-if="item.totalPrice.free" class="text-green-600 font-bold">{{t('shared.common.free')}}</span>
                    <span v-else class="font-bold" v-html="item.totalPrice.price[priceField]"/>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
          <div class="flex items-center justify-between">
            <dt class="text-slate-500">{{ t('shipping') }}
              <template v-if="checkout.deliveryMethod?.title !== ''">({{ checkout.deliveryMethod?.title }})</template>
            </dt>
            <dd v-html="deliveryCost"/>
          </div>
        </div>

        <dl class="hidden space-y-6 border-t border-gray-200 pt-6 font-medium text-gray-900 lg:block">
          <div v-if="displayVatTotals" class="flex items-center justify-between">
            <dt class="text-slate-500">{{ t('total_excl_vat') }}</dt>
            <dd v-html="checkout.cost?.totalPriceExcl?.formatted"/>
          </div>

          <div v-if="displayVatTotals" v-for="vatTotal in checkout.cost.vatTotals" class="flex items-center justify-between">
            <dt class="text-slate-500">{{ t('vat') }} {{vatTotal.percentage.formatted}}</dt>
            <dd v-html="vatTotal.total.formatted"/>
          </div>

          <div class="flex items-center justify-between" :class="displayVatTotals ? 'pt-6 border-t border-gray-200':''">
            <dt v-if="displayVatTotals" class="text-base">{{ t('total_incl_vat') }}</dt>
            <dt v-else class="text-base">{{ t('total') }}</dt>
            <dd class="text-base" v-html="checkout.cost?.totalPriceIncl?.formatted"/>
          </div>
        </dl>
      </div>
    </section>
  </DesktopView>


</template>

<style scoped>

</style>